import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Save, TrendingUp, AlertTriangle, Calendar, CalendarDays } from "lucide-react";
import DeadlineBanner from "@/components/DeadlineBanner";
import MeetingCountdown from "@/components/MeetingCountdown";
import DailyReportGrid from "@/components/DailyReportGrid";
import PerformanceGaps from "@/components/gps/PerformanceGaps";

const init = (week) => ({
  week_number: week, contactos: 0, reunioes: 0, angariacoes: 0, vendas: 0,
  social_posts: 0, humanized_videos: 0, formacoes: 0,
  social_post_url: "", humanized_video_url: "",
  notes: "",
});

export default function SelfReport() {
  const [settings, setSettings] = useState({ current_week: 1, weeks_total: 10 });
  const [form, setForm] = useState(init(1));
  const [saving, setSaving] = useState(false);
  const [linked, setLinked] = useState(null);
  const [history, setHistory] = useState([]);
  const [mode, setMode] = useState("daily"); // "daily" | "weekly"

  const refresh = useCallback(async () => {
    const { data } = await api.get("/my-stats");
    setLinked(data.player);
    setHistory(data.entries || []);
  }, []);

  useEffect(() => {
    api.get("/settings").then((r) => {
      setSettings(r.data);
      const startWeek = r.data.current_week_computed || r.data.current_week || 1;
      setForm((f) => ({ ...f, week_number: startWeek }));
    });
    refresh();
  }, [refresh]);

  const currentWeek = settings.current_week_computed || settings.current_week || 1;
  const editionStarted = (settings.current_week_computed ?? 0) > 0;
  // "Always-open reporting" (Jul/2026): between editions the grid stays open. Only lock BEFORE start.
  const isLocked = !editionStarted;
  const isInterlude = settings.status === "ended"; // between editions — banner-only, does NOT lock
  const weeksTotal = settings.weeks_total || 1;
  // Assistentes não coopetem por Angariações/Vendas — esses campos ficam ocultos no form
  // e no histórico, e os pontos correspondentes não são contabilizados (já tratado no backend).
  const isAssistant = linked?.role_in_team === "assistant";

  useEffect(() => {
    // Prefill current week values if exist
    const e = history.find((x) => x.week_number === form.week_number);
    if (e) {
      setForm(() => ({
        week_number: e.week_number,
        contactos: e.contactos || 0,
        reunioes: e.reunioes || 0,
        angariacoes: e.angariacoes || 0,
        vendas: e.vendas || 0,
        social_posts: e.social_posts || 0,
        humanized_videos: e.humanized_videos || 0,
        formacoes: e.formacoes || 0,
        social_post_url: e.social_post_url || "",
        humanized_video_url: e.humanized_video_url || "",
        notes: e.notes || "",
      }));
    } else {
      setForm((f) => init(f.week_number));
    }
  }, [history, form.week_number]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/entries/activity", form);
      toast.success("Atividade guardada");
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  if (!linked) {
    return (
      <div className="kw-card p-8 max-w-2xl">
        <h2 className="font-display text-2xl font-black uppercase">Conta não associada</h2>
        <p className="text-[#A3A3A3] mt-2">
          A tua conta ainda não está associada a um registo de consultor. Pede ao admin para te criar usando o mesmo email da tua conta.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8 kw-fade-up" data-testid="self-report-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="kw-overline mb-2">Auto-reporte</div>
          <h1 className="font-display text-5xl md:text-6xl font-black uppercase tracking-tighter">
            Os meus <span className="text-[#E50000]">Números</span>
          </h1>
          <p className="text-[#A3A3A3] mt-2 text-sm max-w-2xl">
            Reporta os teus contactos, reuniões realizadas, angariações e vendas. A janela abre automaticamente segunda-feira às 00:00 e fecha domingo às 23:59. A presença, pontualidade e TPC são marcados pelo teu coach ou capitão.
          </p>
        </div>
        <div className="kw-card px-5 py-3">
          <div className="kw-overline">Semana atual</div>
          <div className="font-display text-3xl font-black">Semana {currentWeek}</div>
          {currentWeek <= weeksTotal && (
            <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-1">de {weeksTotal} da edição</div>
          )}
          {isInterlude && (
            <div className="text-[10px] uppercase tracking-widest text-amber-400 mt-1" data-testid="interlude-badge">
              Intervalo entre edições
            </div>
          )}
        </div>
      </div>

      {isInterlude && (
        <div className="kw-card p-4 border-l-4 border-amber-500 bg-amber-500/10" data-testid="interlude-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <div className="font-bold uppercase tracking-widest text-amber-400 text-xs mb-1">Intervalo entre edições</div>
              <p className="text-[#A3A3A3]">
                A edição <span className="text-white">{settings.edition_name || "actual"}</span> terminou, mas o reporte continua aberto.
                Continua a registar contactos, reuniões e vendas — <span className="text-white">pontua para o ranking All-Time</span>.
                As reuniões semanais obrigatórias regressam com a próxima edição.
              </p>
            </div>
          </div>
        </div>
      )}

      {!isInterlude && <DeadlineBanner />}

      {!isInterlude && <MeetingCountdown />}

      {/* Mode toggle: Daily (Seg-Dom grid) vs Weekly (totals + notes/URLs) */}
      <div className="kw-card p-3 inline-flex gap-1" data-testid="self-mode-toggle">
        <button onClick={() => setMode("daily")} disabled={isLocked}
          className={`px-4 py-2 text-xs uppercase tracking-widest font-bold inline-flex items-center gap-2 transition-colors ${mode === "daily" ? "bg-[#E50000] text-white" : "text-[#A3A3A3] hover:text-white"}`}
          data-testid="self-mode-daily">
          <CalendarDays className="w-3.5 h-3.5" /> Diário
        </button>
        <button onClick={() => setMode("weekly")} disabled={isLocked}
          className={`px-4 py-2 text-xs uppercase tracking-widest font-bold inline-flex items-center gap-2 transition-colors ${mode === "weekly" ? "bg-[#E50000] text-white" : "text-[#A3A3A3] hover:text-white"}`}
          data-testid="self-mode-weekly">
          <Calendar className="w-3.5 h-3.5" /> Total semanal + notas
        </button>
      </div>

      {isLocked && (
        <div className="kw-card p-5 flex items-start gap-3 border-l-4" style={{ borderLeftColor: "#FFB020" }} data-testid="lockout-banner">
          <AlertTriangle className="w-5 h-5 text-[#FFB020] mt-1" />
          <div className="text-sm">
            <div className="kw-overline text-[#FFB020]">Reporte bloqueado</div>
            <p className="text-[#A3A3A3] mt-1">
              A liga ainda não começou. Aguarda pela data de início definida em Configuração.
            </p>
          </div>
        </div>
      )}

      {mode === "daily" && linked && (
        <DailyReportGrid playerId={linked.id} week={currentWeek} disabled={isLocked} />
      )}

      {linked && <PerformanceGaps compact />}

      {mode === "weekly" && (
      <form onSubmit={submit} className={`kw-card p-6 grid md:grid-cols-3 gap-4 ${isLocked ? "opacity-60" : ""}`} data-testid="self-form">
        <Field label={`Semana a reportar (semana ${currentWeek} de ${settings.weeks_total})`}>
          <input type="number" className="kw-input" value={currentWeek} disabled readOnly data-testid="self-week" />
        </Field>
        <NumField label="Contactos (+1 cada)" value={form.contactos} disabled={isLocked} onChange={(v) => setForm({ ...form, contactos: v })} testid="self-contactos" />
        <NumField label="Reuniões realizadas (+15)" value={form.reunioes} disabled={isLocked} onChange={(v) => setForm({ ...form, reunioes: v })} testid="self-reunioes" />
        {!isAssistant && (
          <>
            <NumField label="Angariações (+20)" value={form.angariacoes} disabled={isLocked} onChange={(v) => setForm({ ...form, angariacoes: v })} testid="self-angariacoes" />
            <NumField label="Vendas (+50)" value={form.vendas} disabled={isLocked} onChange={(v) => setForm({ ...form, vendas: v })} testid="self-vendas" />
          </>
        )}
        <NumField label="Posts redes sociais (+5)" value={form.social_posts} disabled={isLocked} onChange={(v) => setForm({ ...form, social_posts: v })} testid="self-social-posts" />
        <NumField label="Vídeos humanizados (+10)" value={form.humanized_videos} disabled={isLocked} onChange={(v) => setForm({ ...form, humanized_videos: v })} testid="self-humanized-videos" />
        <NumField label="Formações (+8)" value={form.formacoes} disabled={isLocked} onChange={(v) => setForm({ ...form, formacoes: v })} testid="self-formacoes" />
        <div className="md:col-span-3 grid md:grid-cols-2 gap-4">
          <Field label="URL do post (opcional)">
            <input type="url" className="kw-input" disabled={isLocked} value={form.social_post_url}
              placeholder="https://linkedin.com/posts/..."
              onChange={(e) => setForm({ ...form, social_post_url: e.target.value })} data-testid="self-social-post-url" />
          </Field>
          <Field label="URL do vídeo (opcional)">
            <input type="url" className="kw-input" disabled={isLocked} value={form.humanized_video_url}
              placeholder="https://youtu.be/..."
              onChange={(e) => setForm({ ...form, humanized_video_url: e.target.value })} data-testid="self-humanized-video-url" />
          </Field>
        </div>
        <div className="md:col-span-3">
          <Field label="Notas (opcional)">
            <textarea className="kw-input min-h-[60px]" disabled={isLocked} value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="self-notes" />
          </Field>
        </div>
        <div className="md:col-span-3 flex justify-end">
          <button className="kw-btn-primary inline-flex items-center gap-2" disabled={saving || isLocked} data-testid="self-submit">
            <Save className="w-4 h-4" /> {saving ? "A guardar…" : isLocked ? "Bloqueado" : "Guardar"}
          </button>
        </div>
      </form>
      )}

      <section>
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-5 h-5 text-[#E50000]" />
          <h2 className="font-display text-2xl font-black uppercase">Histórico</h2>
        </div>
        <div className="kw-card overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-[#0A0A0A] border-b border-[#222]">
              <tr className="kw-overline">
                <th className="p-3">Sem.</th>
                <th className="p-3 text-right" title="Contactos">C</th>
                <th className="p-3 text-right" title="Reuniões">R</th>
                {!isAssistant && <th className="p-3 text-right" title="Angariações">A</th>}
                {!isAssistant && <th className="p-3 text-right" title="Vendas">V</th>}
                <th className="p-3 text-right" title="Posts redes sociais">P</th>
                <th className="p-3 text-right" title="Vídeos humanizados">Vid</th>
                <th className="p-3 text-right" title="Formações">F</th>
                <th className="p-3">Pres./Pont.</th>
                <th className="p-3 text-right">Pontos</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 && <tr><td colSpan={isAssistant ? "8" : "10"} className="p-6 text-center text-[#737373]">Sem registos.</td></tr>}
              {history.map((e) => (
                <tr key={e.id} className={`border-b border-[#1A1A1A] ${e.reporting === "nao_reportou" ? "opacity-60" : ""}`}>
                  <td className="p-3">{e.week_number}</td>
                  <td className="p-3 text-right">{e.contactos}</td>
                  <td className="p-3 text-right">{e.reunioes}</td>
                  {!isAssistant && <td className="p-3 text-right">{e.angariacoes}</td>}
                  {!isAssistant && <td className="p-3 text-right">{e.vendas}</td>}
                  <td className="p-3 text-right">
                    {e.social_post_url ? (
                      <a href={e.social_post_url} target="_blank" rel="noopener noreferrer" className="text-[#E50000] hover:underline" title="Abrir post">
                        {e.social_posts || 0}
                      </a>
                    ) : (e.social_posts || 0)}
                  </td>
                  <td className="p-3 text-right">
                    {e.humanized_video_url ? (
                      <a href={e.humanized_video_url} target="_blank" rel="noopener noreferrer" className="text-[#E50000] hover:underline" title="Abrir vídeo">
                        {e.humanized_videos || 0}
                      </a>
                    ) : (e.humanized_videos || 0)}
                  </td>
                  <td className="p-3 text-right">{e.formacoes || 0}</td>
                  <td className="p-3 text-xs">{e.presente ? "Pres." : "Falta"} · {e.pontual ? "Pont." : "Atraso"}</td>
                  <td className="p-3 text-right font-display font-black">{Math.round(e.points)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) { return (<div><label className="kw-overline block mb-2">{label}</label>{children}</div>); }
function NumField({ label, value, onChange, testid, disabled }) {
  return (
    <Field label={label}>
      <input type="number" min="0" className="kw-input" value={value} disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))} data-testid={testid} />
    </Field>
  );
}
